import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"

// Config
import PRConfig from "components/Stacker/config/ProductConfiguratorConfig"

// Utils
import { getS7ConfigUrl } from "lib/utils/productConfig-util"

// Actions
import { setCustom } from "components/Stacker/actions/ProductConfiguratorActions"

// components
import Icon from "components/Stacker/components/Icon/Icon"

// Styles
import "./Step3Monogram.scss"
import { useEffect, useState, useRef } from "react"
import { useDispatch } from "react-redux"
import { useI18nContext } from "i18n/i18n-react"

function Step3Monogram({
  nextStep,
  metal,
  design,
  theme,
  configTypeValue,
  setScroll,
  setContainerHeight,
  containerNode,
  text,
  font,
  fontCode,
}) {
  const node = useRef()
  const { LL } = useI18nContext()
  const dispatch = useDispatch()
  const [enteredValue, set_enteredValue] = useState(false)

  useEffect(() => {
    const updateHeight = () => {
      const computedHeight = getComputedStyle(node.current).height
      setContainerHeight(computedHeight)
    }
    setScroll(containerNode)
    setTimeout(() => {
      setContainerHeight(getComputedStyle(node.current).height)
    }, 300)
    window.addEventListener("resize", updateHeight)
    return () => window.removeEventListener("resize", updateHeight)
  }, [containerNode])

  const setFont = (e) => {
    const {
      value,
      dataset: { fontcode },
    } = e.currentTarget
    const data = {
      font: value,
      fontCode: fontcode,
      text,
    }
    dispatch(setCustom(data))
  }

  const validateInput = (e) => {
    const regex = new RegExp(/[A-Za-z]/gi)
    if (regex.test(e.key)) {
      set_enteredValue(true)
    } else {
      set_enteredValue(false)
    }
  }

  const handleChange = (e) => {
    if (enteredValue) {
      const { value } = e.target
      const data = {
        font,
        fontCode,
        text: value.toUpperCase(),
      }
      dispatch(setCustom(data))
    }
  }

  const imgUrl = getS7ConfigUrl(configTypeValue, theme, metal)

  return (
    <div className="pinky-wrap" ref={node}>
      <div className="container">
        <div
          className={`config-step-content config-${metal}-step3 config-step-content__${design}`}
        >
          <h2
            id="step-title"
            className="text-center config-step-content__step-title"
          >
            {LL.productConfig.selectMonogramText()}
          </h2>

          <div className="app-js__product-config">
            <div className="row d-flex align-items-end">
              <div className="col-12 col-xl-8 product-config-container">
                <div className="step-img-container">
                  <img
                    src={`${imgUrl}?$productConfigMonogram_desktop$`}
                    className="img-fluid"
                    alt=""
                  />
                </div>
                <div
                  className={`pinky-viewer letter-space-${
                    text ? text.length : "0"
                  } font-${font.toLowerCase()} ${metal}-config  d-flex align-items-center justify-content-center`}
                >
                  <div className="pinky-text d-flex align-items-center justify-content-center">
                    <span className="letter-box first-letter">
                      {text.charAt(0)}
                    </span>
                    <span className="letter-box second-letter">
                      {text.charAt(1)}
                    </span>
                    <span className="letter-box third-letter">
                      {text.charAt(2)}
                    </span>
                  </div>
                </div>
              </div>
              <div className="col-12 col-xl-4 config-module-container d-flex">
                <div className="w-100">
                  <div className="category-group">
                    <div className="category-group__title">
                      <h6 className="category-group__title-text">
                        {LL.productConfig.fontLabel()}
                      </h6>
                    </div>
                    <div className="category-group__options d-flex align-items-start">
                      {PRConfig.FONT_VALUES.map((fontVal, i) => (
                        <div
                          key={`${fontVal.value}-key`}
                          className="category-group__options-item"
                        >
                          <button
                            value={fontVal.value}
                            data-fontcode={fontVal.code}
                            onClick={setFont}
                            className={classNames(
                              "category-group__options-item-font-btn",
                              {
                                selected: font === fontVal.value,
                              }
                            )}
                          >
                            <Icon
                              additionalClassNames="category-group__options-item-icons"
                              iconName={`icon-dy-${fontVal.value.toLowerCase()}`}
                            />
                            <p className="category-group__options-item-label">
                              {fontVal.name}
                            </p>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="w-100 category-group pinky-text-value">
                    <div className="category-group__title">
                      <h6 className="category-group__title-text">
                        {LL.productConfig.enterInitialsText()}
                      </h6>
                    </div>
                    <div className="category-group__options d-xl-flex align-items-start justify-content-sm-center">
                      <div className="input-wrapper text-center">
                        <input
                          onKeyDown={validateInput}
                          onChange={handleChange}
                          value={text.toUpperCase() || ""}
                          placeholder={!text ? "ABC" : null}
                          {...PRConfig.PINKY_RING_TEXT}
                        />
                        <p className="input-wrapper__comment">
                          {LL.productConfig.enter3InitialsText()}
                        </p>
                      </div>
                      <button
                        disabled={!font || !text}
                        aria-disabled={!font || !text}
                        className="category-group__options-next-btn c-btn d-flex align-items-center justify-content-center hover-transition"
                        onClick={nextStep}
                      >
                        {LL.productConfig.nextText()}
                      </button>
                    </div>
                  </div>
                  <div className="w-100 pinky-disclaimer-text">
                    <p>{LL.productConfig.step3MonogramText()}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Step3Monogram.propTypes = {
  nextStep: PropTypes.func,
  metal: PropTypes.string.isRequired,
  design: PropTypes.string.isRequired,
  font: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  fontCode: PropTypes.string.isRequired,
  setContainerHeight: PropTypes.func.isRequired,
  setScroll: PropTypes.func,
  theme: PropTypes.string.isRequired,
  containerNode: PropTypes.node,
  configTypeValue: PropTypes.string,
}

export default Step3Monogram
